<template>
  <div class="mg-migrate-shell">
    <v-main>
      <div class="mg-migrate-wrap">
        <!-- หัวเรื่อง -->
        <header class="mg-migrate-head">
          <div class="mg-head-mark"><v-icon color="white">mdi-database-arrow-right</v-icon></div>
          <div class="mg-head-copy">
            <h1>ย้ายข้อมูลร้านไปเวอร์ชันใหม่</h1>
            <p>{{ shopName || 'ร้านนี้' }} <span class="mg-head-id">{{ shopId }}</span></p>
          </div>
        </header>

        <!-- แถบขั้นตอน -->
        <nav class="mg-stepbar">
          <div
            v-for="(step, index) in steps"
            :key="step.key"
            class="mg-stepbar-item"
            :class="'mg-is-' + stepState(index)"
          >
            <span class="mg-step-mark">
              <v-icon v-if="stepState(index) === 'done'" size="18" color="white">mdi-check</v-icon>
              <span v-else>{{ String(index + 1).padStart(2, '0') }}</span>
            </span>
            <span
              v-if="index < steps.length - 1"
              class="mg-step-line"
              :class="{ 'mg-step-line--on': stepState(index) === 'done' }"
            ></span>
            <span class="mg-step-copy">
              <span class="mg-step-label">{{ step.label }}</span>
              <span class="mg-step-status">{{ stepStatusLabel(index) }}</span>
            </span>
          </div>
        </nav>

        <div v-if="isDevApi" class="mg-env-note">
          <v-icon small color="primary">mdi-flask-outline</v-icon>
          เว็บชุดทดสอบ — หน้านี้ยิงไปที่ API dev ({{ migrationApiBase }}) ไม่ใช่ production
        </div>

        <v-alert v-if="!shopId" type="warning" dense outlined class="mb-4">
          ไม่พบร้านในเซสชันการเข้าสู่ระบบ กรุณาเข้าสู่ระบบใหม่อีกครั้ง
        </v-alert>

        <!-- ขั้น 1: ตรวจสอบข้อมูล -->
        <section class="mg-panel">
          <div class="mg-panel-head">
            <div>
              <h2>ตรวจสอบข้อมูลที่จะย้าย</h2>
              <p>อ่านอย่างเดียวจากระบบเก่า ไม่ลบไม่แก้ไขข้อมูลเดิม</p>
            </div>
            <v-btn color="primary" depressed :loading="previewLoading" :disabled="!shopId || jobRunning" @click="previewShop">
              <v-icon left small>mdi-magnify</v-icon>ตรวจสอบข้อมูล
            </v-btn>
          </div>

          <v-alert v-if="previewError" type="error" dense outlined class="mb-0">{{ previewError }}</v-alert>

          <template v-if="preview">
            <div class="mg-tiles">
              <div class="mg-tile">
                <span>สมาชิก</span>
                <strong>{{ formatNumber(preview.memberCount) }}</strong>
              </div>
              <div class="mg-tile">
                <span>รายการทั้งหมด</span>
                <strong>{{ formatNumber(preview.totalCount || preview.memberCount) }}</strong>
              </div>
              <div class="mg-tile">
                <span>ชนิดข้อมูลที่มีของ</span>
                <strong>{{ filledBreakdown.length }}</strong>
              </div>
            </div>

            <div class="mg-chips">
              <span v-for="row in filledBreakdown" :key="row.entityType" class="mg-chip">
                {{ row.label }}<b>{{ formatNumber(row.count) }}</b>
              </span>
            </div>
          </template>
          <p v-else-if="!previewLoading" class="mg-empty">ยังไม่ได้ตรวจสอบ — กดปุ่มด้านบนเพื่อดูว่ามีข้อมูลอะไรบ้าง</p>
        </section>

        <!-- ขั้น 2: ย้ายข้อมูล -->
        <section v-if="preview" class="mg-panel">
          <div class="mg-panel-head">
            <div>
              <h2>ย้ายข้อมูล</h2>
              <p>ทดลองก่อนอย่างน้อยหนึ่งครั้ง แล้วค่อยย้ายจริง</p>
            </div>
          </div>

          <v-alert v-if="realMigrationDone" type="info" dense outlined class="mb-3 mg-locked-note">
            ร้านนี้ย้ายข้อมูลจริงเสร็จแล้วในเซสชันนี้ ปุ่มย้ายถูกล็อกไว้ ไปต่อที่ขั้นตั้งค่า LIFF Login ได้เลย
            <v-btn text small color="primary" class="ml-2" @click="resetMigration">ล้างสถานะแล้วย้ายใหม่</v-btn>
          </v-alert>

          <div class="mg-run-actions">
            <v-btn outlined color="primary" :loading="startLoading && mode === 'dry-run'" :disabled="jobRunning || realMigrationDone" @click="requestDryRun">
              <v-icon left small>mdi-flask-outline</v-icon>ทดลองย้าย (dry-run)
            </v-btn>
            <v-btn color="error" depressed :loading="startLoading && mode === 'real'" :disabled="jobRunning || realMigrationDone" @click="confirmRealMigration">
              <v-icon left small>mdi-play</v-icon>ย้ายข้อมูลจริง
            </v-btn>
            <span class="mg-run-note">ย้ายจริงแล้วยกเลิกกลางคันไม่ได้</span>
          </div>

          <v-alert v-if="startError" type="error" dense outlined class="mt-3 mb-0">{{ startError }}</v-alert>

          <div v-if="targetShopId" class="mg-target">
            <v-icon small :color="shopCreated ? 'success' : 'primary'">{{ shopCreated ? 'mdi-store-plus' : 'mdi-store' }}</v-icon>
            <span>{{ shopCreated ? 'สร้างร้านในระบบใหม่ให้แล้ว' : 'ร้านปลายทางในระบบใหม่' }}</span>
            <strong>{{ targetShopName }}</strong>
            <code>{{ targetShopId }}</code>
          </div>

          <div v-if="job" class="mg-progress">
            <div class="mg-progress-top">
              <div>
                <span class="mg-progress-status" :class="'mg-st-' + job.status">{{ statusLabel }}</span>
                <span class="mg-progress-sub">ย้ายแล้ว {{ formatNumber(job.processedCount) }} จาก {{ formatNumber(job.totalCount) || '?' }} รายการ<template v-if="mode === 'dry-run'"> · โหมดทดลอง</template></span>
              </div>
              <strong class="mg-progress-percent">{{ percentText }}%</strong>
            </div>
            <v-progress-linear :value="job.percent" height="10" rounded :color="progressColor" />

            <div class="mg-counts">
              <div><strong>{{ formatNumber(job.createdCount) }}</strong><span>สร้างใหม่</span></div>
              <div><strong>{{ formatNumber(job.updatedCount) }}</strong><span>อัปเดต</span></div>
              <div><strong>{{ formatNumber(job.skippedCount) }}</strong><span>ข้าม</span></div>
              <div :class="job.errorCount > 0 ? 'mg-is-error' : ''"><strong>{{ formatNumber(job.errorCount) }}</strong><span>ผิดพลาด</span></div>
            </div>

            <v-alert v-if="job.status === 'failed'" type="error" dense outlined class="mt-3 mb-0">{{ job.lastError || 'งานย้ายล้มเหลว' }}</v-alert>
            <v-alert v-else-if="job.status === 'completed'" type="success" dense outlined class="mt-3 mb-0">
              ย้ายข้อมูลเสร็จสิ้น ({{ mode === 'dry-run' ? 'โหมดทดลอง ยังไม่ได้เขียนข้อมูลจริง' : 'เขียนข้อมูลจริงแล้ว' }})
            </v-alert>

            <v-data-table
              v-if="recentItems.length"
              :headers="itemHeaders"
              :items="recentItems"
              :items-per-page="10"
              dense
              class="mt-4 mg-recent"
            ></v-data-table>
          </div>
        </section>

        <!-- ขั้น 3: LIFF Login -->
        <section v-if="realMigrationDone" class="mg-panel">
          <div class="mg-panel-head">
            <div>
              <h2>ตั้งค่า LIFF Login</h2>
              <p>สร้างหน้า LIFF สำหรับเข้าสู่ระบบของร้านบนเวอร์ชันใหม่</p>
            </div>
            <v-btn text color="primary" :loading="liffLoading" @click="loadLiff">
              <v-icon left small>mdi-refresh</v-icon>ตรวจสถานะ
            </v-btn>
          </div>

          <v-alert v-if="liffError" type="error" dense outlined class="mb-0">{{ liffError }}</v-alert>

          <template v-if="liff">
            <div class="mg-liff-status" :class="'mg-lf-' + liff.status">
              <v-icon :color="liffStatusUI.color">{{ liffStatusUI.icon }}</v-icon>
              <div>
                <strong>{{ liffStatusUI.title }}</strong>
                <span>{{ liffStatusUI.message }}</span>
              </div>
            </div>

            <ul v-if="liff.blockedReasons && liff.blockedReasons.length" class="mg-blockers">
              <li v-for="(reason, i) in liff.blockedReasons" :key="i">{{ reason }}</li>
            </ul>

            <div v-if="liff.existing" class="mg-liff-card">
              <div><span>LIFF ID</span><code>{{ liff.existing.liffId }}</code></div>
              <div><span>หน้า</span><code>{{ liff.existing.page }}</code></div>
              <div><span>ปลายทาง</span><code>{{ liff.existing.endpointUrl }}</code></div>
            </div>

            <v-btn
              v-if="liff.status === 'READY'"
              color="primary"
              depressed
              class="mt-4"
              :loading="liffRunLoading"
              @click="runLiff"
            >
              <v-icon left small>mdi-link-variant-plus</v-icon>สร้าง LIFF Login
            </v-btn>
          </template>
          <p v-else-if="!liffLoading && !liffError" class="mg-empty">กด "ตรวจสถานะ" เพื่อดูว่าร้านนี้มี LIFF Login แล้วหรือยัง</p>
        </section>

        <!-- ขั้น 4: เสร็จสิ้น -->
        <section v-if="allDone" class="mg-panel mg-done-panel">
          <v-icon size="34" color="success">mdi-check-decagram</v-icon>
          <div class="mg-done-copy">
            <h2>ย้ายร้านเรียบร้อย</h2>
            <p>ข้อมูลและ LIFF Login พร้อมใช้งานบนเวอร์ชันใหม่แล้ว</p>
          </div>
          <v-btn
            class="mg-done-go"
            color="success"
            depressed
            :href="shopWebUrl"
            target="_blank"
            rel="noopener"
          >
            <v-icon left small>mdi-open-in-new</v-icon>ไปที่เวอร์ชันใหม่
          </v-btn>
        </section>
      </div>
    </v-main>

    <v-dialog v-model="confirmDialog" max-width="480px">
      <v-card class="mg-confirm-card">
        <v-card-title>{{ pendingMode === 'real' ? 'ยืนยันย้ายข้อมูลจริง' : 'ยืนยันทดลองย้าย' }}</v-card-title>
        <v-card-text>
          <template v-if="pendingMode === 'real'">ร้าน <strong>{{ shopName || shopId }}</strong> จะถูกคัดลอกข้อมูล
          <strong>{{ formatNumber((preview && (preview.totalCount || preview.memberCount)) || 0) }}</strong>
          รายการเข้าระบบใหม่จริง (ไม่กระทบข้อมูลระบบเก่า)</template>
          <template v-else>ทดลองย้ายไม่เขียนข้อมูลจริง แต่ยังต้องยืนยันสิทธิ์เพราะเป็นการสั่งงานให้ระบบใหม่</template>
          <v-alert v-if="pendingMode === 'real'" type="warning" dense outlined class="mg-confirm-alert">
            งานที่เริ่มแล้วยกเลิกกลางคันไม่ได้ และยังไม่มีการย้อนกลับอัตโนมัติ
          </v-alert>
          <div class="mg-confirm-user">ยืนยันด้วยรหัสผ่านของ <strong>{{ userName || 'ผู้ใช้ร้านนี้' }}</strong></div>
          <v-text-field
            v-model="adminPassword"
            :type="adminPasswordShow ? 'text' : 'password'"
            :append-icon="adminPasswordShow ? 'mdi-eye-off' : 'mdi-eye'"
            label="รหัสผ่าน"
            dense
            outlined
            hide-details
            autocomplete="current-password"
            @click:append="adminPasswordShow = !adminPasswordShow"
            @keyup.enter="proceedRealMigration"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">ยกเลิก</v-btn>
          <v-btn :color="pendingMode === 'real' ? 'error' : 'primary'" depressed :disabled="!adminPassword" @click="proceedRealMigration">
            {{ pendingMode === 'real' ? 'ยืนยันย้ายจริง' : 'เริ่มทดลองย้าย' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from 'axios'

export default {
  data () {
    const session = this.$session.getAll()
    const sessionData = (session && session.data) || {}
    return {
      session: session,
      shopId: sessionData.shopId || '',
      userName: sessionData.userName || '',
      shopName: sessionData.shopName || sessionData.name || '',
      previewLoading: false,
      previewError: '',
      preview: null,
      startLoading: false,
      startError: '',
      mode: 'dry-run',
      jobId: '',
      job: null,
      targetShopId: '',
      targetShopName: '',
      shopCreated: false,
      recentItems: [],
      confirmDialog: false,
      pendingMode: 'real',
      pollTimer: null,
      steps: [
        { key: 'preview', label: 'ตรวจสอบข้อมูล' },
        { key: 'migrate', label: 'ย้ายข้อมูล' },
        { key: 'liff', label: 'ตั้งค่า LIFF Login' },
        { key: 'done', label: 'เสร็จสิ้น' }
      ],
      adminPassword: '',
      adminPasswordShow: false,
      liff: null,
      liffLoading: false,
      liffRunLoading: false,
      liffError: '',
      entityLabels: {
        level: 'ระดับสมาชิก',
        exchange_rate: 'อัตราแลกแต้ม',
        reward: 'ของรางวัล',
        package: 'แพ็กเกจ',
        promotion: 'โปรโมชัน',
        activity: 'กิจกรรม',
        member: 'สมาชิก',
        member_point: 'แต้มคงเหลือ',
        point_log: 'ประวัติแต้ม',
        member_credit: 'เครดิตคงเหลือ',
        credit_log: 'ประวัติเครดิต',
        member_address: 'ที่อยู่จัดส่ง',
        reward_stock: 'โค้ดของรางวัล',
        package_member: 'แพ็กเกจของสมาชิก',
        user: 'ผู้ใช้เข้าระบบ',
        feature_flag: 'เปิดใช้ฟีเจอร์',
        qr_lot: 'ล็อต QR',
        audience: 'กลุ่มเป้าหมาย',
        custom_field: 'ฟิลด์กำหนดเอง',
        payment: 'ช่องทางชำระเงิน',
        broadcast: 'ประวัติบรอดแคสต์',
        otp: 'ประวัติ OTP',
        notify: 'การแจ้งเตือน',
        page_visit: 'สถิติเข้าชมหน้า'
      },
      itemHeaders: [
        { text: 'ชนิดข้อมูล', value: 'entityLabel' },
        { text: 'Legacy Id', value: 'legacyId' },
        { text: 'การทำงาน', value: 'action' },
        { text: 'รายละเอียด', value: 'message' },
        { text: 'เวลา', value: 'createdAt' }
      ]
    }
  },
  computed: {
    // หน้านี้เป็นหน้าเดียวในแอปที่เขียนข้อมูลข้ามระบบเป็นล้านแถวได้ด้วยปุ่มเดียว
    // main.js ตั้ง DNS_IP เป็น production ไว้แม้ตอนรัน dev (บรรทัด dev ถูก comment ทิ้ง)
    // ถ้าปล่อยไว้ กด "ย้ายข้อมูลจริง" จากเครื่อง dev = ย้ายร้าน production จริง
    // จึงบังคับให้หน้านี้ยิง API dev เสมอเมื่อรันโหมด development
    migrationApiBase () {
      return this.isDevApi ? this.IPPotocalENV_Developer : this.DNS_IP
    },
    // ตัดสินจาก "เว็บนี้คือเว็บอะไร" ไม่ใช่จาก NODE_ENV เพราะเว็บ dev ที่ deploy แล้ว
    // ถูก build เป็น production เหมือนกัน ถ้าดูแค่ NODE_ENV เว็บ dev จะไปยิง API prod
    // แล้วปุ่ม "ย้ายข้อมูลจริง" บนเว็บ dev = ย้ายร้าน production จริง
    isDevApi () {
      if (process.env.NODE_ENV === 'development') return true
      const host = (typeof window !== 'undefined' && window.location && window.location.hostname) || ''
      return host === 'localhost' || host === '127.0.0.1' || /(^|[.-])dev([.-]|$)/.test(host)
    },
    shopWebUrl () {
      return this.isDevApi ? 'https://shop-web-dev.betaskthai.tech' : 'https://shop-web.betaskthai.tech'
    },
    jobRunning () {
      return !!this.job && this.job.status === 'running'
    },
    percentText () {
      if (!this.job) return '0'
      return Math.round(this.job.percent)
    },
    progressColor () {
      if (!this.job) return 'primary'
      if (this.job.status === 'failed') return 'error'
      if (this.job.status === 'completed') return 'success'
      return 'primary'
    },
    filledBreakdown () {
      if (!this.preview || !this.preview.breakdown) return []
      return this.preview.breakdown.filter(row => Number(row.count) > 0)
    },
    realMigrationDone () {
      return !!this.job && this.job.status === 'completed' && this.mode === 'real' && !!this.targetShopId
    },
    liffDone () {
      return !!this.liff && this.liff.status === 'EXISTS'
    },
    allDone () {
      return this.realMigrationDone && this.liffDone
    },
    liffStatusUI () {
      const map = {
        EXISTS: { icon: 'mdi-check-circle', color: 'success', title: 'มี LIFF Login แล้ว', message: 'ร้านนี้มีหน้า LIFF สำหรับเข้าสู่ระบบอยู่แล้ว ไม่ต้องสร้างซ้ำ' },
        READY: { icon: 'mdi-link-variant-plus', color: 'primary', title: 'พร้อมสร้าง LIFF Login', message: 'ระบบจะสร้าง LIFF app ใหม่บน LINE Login channel ของร้าน' },
        BLOCKED: { icon: 'mdi-alert-circle', color: 'warning', title: 'ยังสร้างไม่ได้', message: 'ต้องแก้รายการด้านล่างก่อน แล้วกดตรวจสถานะใหม่' }
      }
      return map[(this.liff && this.liff.status) || ''] || { icon: 'mdi-progress-clock', color: 'grey', title: 'กำลังตรวจสอบ', message: 'โปรดรอสักครู่' }
    },
    statusLabel () {
      if (!this.job) return ''
      const map = {
        running: 'กำลังย้าย...',
        completed: 'เสร็จสิ้น',
        failed: 'ล้มเหลว',
        pending: 'รอเริ่ม'
      }
      return map[this.job.status] || this.job.status
    }
  },
  beforeCreate () {
    if (!this.$session.exists()) {
      this.$router.push('/Core/Login')
    }
  },
  created () {
    this.restoreMigration()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    migrationStorageKey () {
      return 'migrateToV2:' + this.shopId
    },
    saveMigration () {
      if (!this.realMigrationDone) return
      try {
        sessionStorage.setItem(this.migrationStorageKey(), JSON.stringify({
          jobId: this.jobId,
          targetShopId: this.targetShopId,
          targetShopName: this.targetShopName,
          shopCreated: this.shopCreated,
          job: this.job
        }))
      } catch (e) {}
    },
    restoreMigration () {
      if (!this.shopId) return
      let saved = null
      try {
        saved = JSON.parse(sessionStorage.getItem(this.migrationStorageKey()) || 'null')
      } catch (e) {}
      if (!saved || !saved.targetShopId || !saved.job || saved.job.status !== 'completed') return
      this.mode = 'real'
      this.jobId = saved.jobId || ''
      this.targetShopId = saved.targetShopId
      this.targetShopName = saved.targetShopName || ''
      this.shopCreated = !!saved.shopCreated
      this.job = saved.job
      this.previewShop()
      this.loadLiff()
    },
    resetMigration () {
      try {
        sessionStorage.removeItem(this.migrationStorageKey())
      } catch (e) {}
      this.mode = 'dry-run'
      this.jobId = ''
      this.targetShopId = ''
      this.targetShopName = ''
      this.shopCreated = false
      this.job = null
      this.recentItems = []
      this.liff = null
      this.liffError = ''
      this.startError = ''
    },
    formatNumber (value) {
      const n = Number(value || 0)
      return isNaN(n) ? '0' : n.toLocaleString()
    },
    // สถานะของแต่ละขั้น: เสร็จแล้ว / กำลังทำ / ยังไม่ถึง
    stepState (index) {
      const done = [
        !!this.preview,
        this.realMigrationDone,
        this.liffDone,
        this.allDone
      ]
      if (done[index]) return 'done'
      const firstPending = done.findIndex(v => !v)
      return index === firstPending ? 'active' : 'locked'
    },
    stepStatusLabel (index) {
      const state = this.stepState(index)
      if (state === 'done') return 'เสร็จแล้ว'
      if (state === 'active') return 'กำลังดำเนินการ'
      return 'รอดำเนินการ'
    },
    // แปลง error ดิบของ axios เป็นสาเหตุที่คนอ่านแล้วรู้ว่าต้องทำอะไรต่อ
    describeLiffError (error, fallback) {
      const status = error && error.response && error.response.status
      if (status === 404) {
        return 'เซิร์ฟเวอร์ยังไม่มีเส้น /migration/liff — ต้อง deploy ฝั่ง API ก่อน ' +
          '(ที่ยิงอยู่: ' + this.migrationApiBase + ')'
      }
      const message = error && error.response && error.response.data && error.response.data.message
      return fallback + ': ' + (message || (error && error.message) || error)
    },
    async loadLiff () {
      if (!this.shopId || !this.targetShopId) return
      this.liffLoading = true
      this.liffError = ''
      try {
        const response = await axios.get(this.migrationApiBase + '/migration/liff/preview', {
          params: { shopId: this.shopId, targetShopId: this.targetShopId }
        })
        const rs = response.data
        if (rs.status === true) this.liff = rs.data
        else this.liffError = rs.message || 'อ่านสถานะ LIFF ไม่สำเร็จ'
      } catch (error) {
        this.liffError = this.describeLiffError(error, 'อ่านสถานะ LIFF ไม่สำเร็จ')
      } finally {
        this.liffLoading = false
      }
    },
    async runLiff () {
      this.liffRunLoading = true
      this.liffError = ''
      try {
        const response = await axios.post(this.migrationApiBase + '/migration/liff/run', {
          shopId: this.shopId,
          targetShopId: this.targetShopId,
          userName: this.userName,
          userPassword: this.adminPassword
        })
        const rs = response.data
        if (rs.status === true) await this.loadLiff()
        else this.liffError = rs.message || 'สร้าง LIFF Login ไม่สำเร็จ'
      } catch (error) {
        this.liffError = this.describeLiffError(error, 'สร้าง LIFF Login ไม่สำเร็จ')
      } finally {
        this.liffRunLoading = false
      }
    },
    // dry-run ก็เขียนแถวงานฝั่งเซิร์ฟเวอร์ จึงต้องพิสูจน์สิทธิ์เหมือนกัน
    requestDryRun () {
      if (!this.adminPassword) {
        this.pendingMode = 'dry-run'
        this.confirmDialog = true
        return
      }
      this.startMigration('dry-run')
    },
    async previewShop () {
      this.previewLoading = true
      this.previewError = ''
      this.preview = null
      try {
        const response = await axios.get(this.migrationApiBase + '/migration/preview/' + this.shopId)
        if (response.data && response.data.status) {
          this.preview = response.data
        } else {
          this.previewError = (response.data && response.data.message) || 'ตรวจสอบข้อมูลไม่สำเร็จ'
        }
      } catch (error) {
        this.previewError = 'เรียก API ไม่สำเร็จ: ' + error.message
      } finally {
        this.previewLoading = false
      }
    },
    confirmRealMigration () {
      this.pendingMode = 'real'
      this.confirmDialog = true
    },
    proceedRealMigration () {
      if (!this.adminPassword) return
      this.confirmDialog = false
      this.startMigration(this.pendingMode)
    },
    async startMigration (mode) {
      try {
        sessionStorage.removeItem(this.migrationStorageKey())
      } catch (e) {}
      this.mode = mode
      this.startLoading = true
      this.startError = ''
      this.job = null
      this.recentItems = []
      try {
        const response = await axios.post(this.migrationApiBase + '/migration/start', {
          userName: this.userName,
          userPassword: this.adminPassword,
          shopId: this.shopId,
          mode: mode,
          createdUserId: (this.session && this.session.userId) || ''
        })
        if (response.data && response.data.status) {
          this.jobId = response.data.jobId
          this.targetShopId = response.data.shopId || ''
          this.targetShopName = response.data.shopName || ''
          this.shopCreated = !!response.data.shopCreated
          this.pollStatus()
        } else {
          this.startError = (response.data && response.data.message) || 'เริ่มงานย้ายไม่สำเร็จ'
        }
      } catch (error) {
        this.startError = 'เรียก API ไม่สำเร็จ: ' + error.message
      } finally {
        this.startLoading = false
      }
    },
    async pollStatus () {
      this.stopPolling()
      const tick = async () => {
        try {
          const response = await axios.get(this.migrationApiBase + '/migration/status/' + this.jobId)
          if (response.data && response.data.status) {
            this.job = response.data.job
            this.recentItems = (response.data.recentItems || []).map(item => ({
              ...item,
              entityLabel: this.entityLabels[item.entityType] || item.entityType || '-'
            }))
            if (this.job && this.job.status === 'running') {
              this.pollTimer = setTimeout(tick, 2000)
            } else {
              this.saveMigration()
            }
          }
        } catch (error) {
          this.startError = 'ดึงสถานะไม่สำเร็จ: ' + error.message
        }
      }
      await tick()
    },
    stopPolling () {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    }
  }
}
</script>

<style scoped>
.mg-migrate-shell { background: #f6f7fb; }
/* พื้นหลังต้องเต็มพื้นที่เนื้อหาเสมอ ไม่ใช่จบตรงที่การ์ดสุดท้าย แล้วเหลือแถบขาวข้างล่าง */
.mg-migrate-shell >>> .v-main { background: #f6f7fb; min-height: 100vh; }
.mg-migrate-wrap { max-width: 1080px; margin: 0 auto; padding: 28px 20px 60px; }

/* หัวเรื่อง */
.mg-migrate-head { display: flex; align-items: center; gap: 14px; margin-bottom: 22px; }
.mg-head-mark {
  width: 46px; height: 46px; border-radius: 13px; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #4f6ef7, #7b5cf0); box-shadow: 0 6px 16px rgba(79, 110, 247, .3);
}
.mg-head-copy h1 { font-size: 1.3rem; font-weight: 700; margin: 0; color: #1c1c28; line-height: 1.3; }
.mg-head-copy p { margin: 3px 0 0; font-size: .85rem; color: #6b7280; }
.mg-head-id { font-family: ui-monospace, monospace; font-size: .74rem; color: #9aa1ad; margin-left: 6px; }

/* แถบขั้นตอน */
.mg-stepbar {
  display: flex; background: #fff; border-radius: 14px; padding: 18px 20px; margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, .06);
}
.mg-stepbar-item { position: relative; flex: 1; display: flex; flex-direction: column; align-items: center; text-align: center; }
.mg-step-mark {
  width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: .8rem; font-weight: 700; background: #eceef3; color: #98a2b3; position: relative; z-index: 1;
  transition: background .2s, color .2s, box-shadow .2s;
}
/* เส้นเชื่อมวาดครั้งเดียวต่อหนึ่งช่วง จากขอบวงกลมนี้ไปจรดขอบวงกลมถัดไป
   (เดิมแยกเป็นครึ่งซ้าย+ครึ่งขวา ทำให้มีรอยต่อคนละสีและดูเหมือนเส้นขาด) */
.mg-step-line {
  position: absolute; top: 16px; height: 2px; background: #eceef3; z-index: 0;
  left: calc(50% + 21px); width: calc(100% - 42px);
}
.mg-step-copy { margin-top: 9px; display: flex; flex-direction: column; gap: 2px; }
.mg-step-label { font-size: .84rem; font-weight: 600; color: #98a2b3; }
.mg-step-status { font-size: .7rem; color: #b6bcc7; }

.mg-is-done .mg-step-mark { background: #12b76a; color: #fff; }
.mg-is-done .mg-step-label { color: #344054; }
.mg-step-line--on { background: #12b76a; }
.mg-is-active .mg-step-mark { background: #4f6ef7; color: #fff; box-shadow: 0 0 0 4px rgba(79, 110, 247, .16); }
.mg-is-active .mg-step-label { color: #1c1c28; }
.mg-is-active .mg-step-status { color: #4f6ef7; font-weight: 600; }

/* การ์ดเนื้อหา */
.mg-panel { background: #fff; border-radius: 14px; padding: 22px 24px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(16, 24, 40, .06); }
.mg-panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
.mg-panel-head h2 { font-size: 1.02rem; font-weight: 700; margin: 0; color: #1c1c28; }
.mg-panel-head p { margin: 3px 0 0; font-size: .82rem; color: #6b7280; }
.mg-empty { font-size: .84rem; color: #98a2b3; margin: 0; }
.mg-confirm-user { font-size: .82rem; color: #475467; margin: 16px 0 10px; }
.mg-env-note {
  display: flex; align-items: center; gap: 8px; font-size: .78rem; color: #4f6ef7;
  background: #eef2ff; border-radius: 10px; padding: 9px 14px; margin-bottom: 16px;
}

/* ตัวเลขสรุป */
.mg-tiles { display: flex; flex-wrap: wrap; gap: 12px; }
.mg-tile { flex: 1 1 170px; border: 1px solid #eaecf0; border-radius: 11px; padding: 14px 16px; }
.mg-tile span { display: block; font-size: .78rem; color: #6b7280; }
.mg-tile strong { font-size: 1.55rem; font-weight: 700; color: #1c1c28; line-height: 1.3; }

.mg-chips { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 14px; }
.mg-chip { font-size: .76rem; background: #f4f5f8; border-radius: 999px; padding: 4px 11px; color: #475467; }
.mg-chip b { margin-left: 6px; color: #1c1c28; }

/* ปุ่มย้าย */
.mg-run-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
.mg-run-note { font-size: .76rem; color: #98a2b3; }
.mg-locked-note { font-size: .85rem; }
.mg-locked-note .v-btn { text-transform: none; letter-spacing: 0; }

.mg-target { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 16px; font-size: .84rem; color: #475467; }
.mg-target strong { color: #1c1c28; }
.mg-target code, .mg-liff-card code { font-family: ui-monospace, monospace; font-size: .74rem; background: #f4f5f8; border-radius: 6px; padding: 2px 7px; color: #475467; }

/* ความคืบหน้า */
.mg-progress { margin-top: 22px; padding-top: 20px; border-top: 1px solid #eff1f4; }
.mg-progress-top { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; margin-bottom: 9px; }
.mg-progress-status { display: inline-block; font-size: .78rem; font-weight: 700; padding: 2px 10px; border-radius: 999px; background: #eef2ff; color: #4f6ef7; }
.mg-progress-status.mg-st-completed { background: #e7f8ef; color: #12b76a; }
.mg-progress-status.mg-st-failed { background: #fdecec; color: #e5484d; }
.mg-progress-sub { display: block; font-size: .8rem; color: #6b7280; margin-top: 5px; }
.mg-progress-percent { font-size: 1.5rem; font-weight: 700; color: #1c1c28; }

.mg-counts { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px; }
.mg-counts > div { flex: 1 1 110px; text-align: center; border: 1px solid #eaecf0; border-radius: 10px; padding: 11px 8px; }
.mg-counts strong { display: block; font-size: 1.2rem; color: #1c1c28; }
.mg-counts span { font-size: .74rem; color: #6b7280; }
.mg-counts .mg-is-error strong { color: #e5484d; }
.mg-recent >>> .v-data-table__wrapper { font-size: .8rem; }

/* LIFF */
.mg-liff-status { display: flex; align-items: flex-start; gap: 12px; border-radius: 11px; padding: 14px 16px; background: #f8f9fc; }
.mg-liff-status strong { display: block; font-size: .9rem; color: #1c1c28; }
.mg-liff-status span { font-size: .8rem; color: #6b7280; }
.mg-liff-status.mg-lf-EXISTS { background: #f1fbf6; }
.mg-liff-status.mg-lf-BLOCKED { background: #fff8ef; }
.mg-blockers { margin: 12px 0 0; padding-left: 20px; font-size: .82rem; color: #b54708; }
.mg-liff-card { margin-top: 14px; border: 1px solid #eaecf0; border-radius: 11px; padding: 12px 16px; }
.mg-liff-card > div { display: flex; align-items: center; gap: 10px; padding: 5px 0; font-size: .82rem; }
.mg-liff-card span { min-width: 70px; color: #6b7280; }

/* เสร็จสิ้น */
.mg-done-panel { display: flex; align-items: center; gap: 16px; background: linear-gradient(135deg, #f1fbf6, #ffffff); }
.mg-done-panel h2 { font-size: 1.05rem; font-weight: 700; margin: 0; color: #1c1c28; }
.mg-done-panel p { margin: 3px 0 0; font-size: .84rem; color: #6b7280; }
.mg-done-copy { flex: 1 1 auto; }
.mg-done-go { flex: 0 0 auto; }

/* App.vue ตั้ง padding ของ v-card เป็น 0 ทั้งแอป ต้องคืนค่าให้ dialog นี้เอง */
.mg-confirm-card { border-radius: 14px !important; }
.mg-confirm-card >>> .v-card__title { padding: 24px 24px 12px !important; font-size: 1.15rem; font-weight: 700; line-height: 1.4; color: #1c1c28; }
.mg-confirm-card >>> .v-card__text { padding: 0 24px 24px !important; font-size: .9rem; line-height: 1.6; color: #475467; }
.mg-confirm-card >>> .v-card__actions { padding: 0 24px 24px; gap: 8px; }
.mg-confirm-card >>> .v-text-field { padding: 0; margin: 0; }
.mg-confirm-card >>> .mg-confirm-alert { margin: 16px 0 0; }

@media (max-width: 860px) {
  .mg-migrate-wrap { padding: 16px 12px 48px; }
  .mg-migrate-head { gap: 11px; }
  .mg-head-mark { width: 38px; height: 38px; border-radius: 11px; }
  .mg-head-copy h1 { font-size: 1.08rem; }
  .mg-head-id { display: block; margin: 2px 0 0; }

  /* ขั้นตอนเลื่อนแนวนอนได้ ไม่บีบจนตัวหนังสือซ้อนกัน */
  .mg-stepbar { padding: 14px 12px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .mg-stepbar-item { flex: 0 0 132px; }
  .mg-step-status { display: none; }

  .mg-panel { padding: 16px 14px; }
  .mg-panel-head { flex-direction: column; align-items: stretch; gap: 10px; }
  .mg-panel-head .v-btn { width: 100%; }
  .mg-tiles { gap: 10px; }
  .mg-tile { flex: 1 1 100%; padding: 12px 14px; }
  .mg-tile strong { font-size: 1.35rem; }
  .mg-run-actions { flex-direction: column; align-items: stretch; }
  .mg-run-actions .v-btn { width: 100%; }
  .mg-run-note { text-align: center; }
  .mg-counts > div { flex: 1 1 calc(50% - 10px); }
  .mg-target { font-size: .8rem; }
  .mg-liff-card > div { flex-direction: column; align-items: flex-start; gap: 2px; }
  .mg-done-panel { flex-wrap: wrap; }
  .mg-done-go { width: 100%; }
  .mg-confirm-card >>> .v-card__title { padding: 20px 18px 10px !important; }
  .mg-confirm-card >>> .v-card__text { padding: 0 18px 18px !important; }
  .mg-confirm-card >>> .v-card__actions { padding: 0 18px 18px; }
}
</style>



// WEBPACK FOOTER //
// src/components/System/MigrateToV2.vue